import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import toastUtils from "../../utils/toastUtils";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  Grid,
  Paper,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  btnLeft: {
    display: "flex",
    marginRight: "auto",
    marginLeft: 12,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
}));

const DialogflowSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  // projectName: Yup.string()
  //   .min(3, "Too Short!")
  //   .max(100, "Too Long!")
  //   .required(),
  // jsonContent: Yup.string().min(3, "Too Short!").required(),
  // language: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required(),
});

const QueueIntegrationModal = ({ open, onClose, integrationId }) => {
  const classes = useStyles();

  const initialState = {
    type: "typebot",
    name: "",
    projectName: "",
    jsonContent: "",
    language: "",
    urlN8N: "",
    typebotDelayMessage: 1000,
    typebotExpires: 1,
    typebotKeywordFinish: "",
    typebotKeywordRestart: "",
    typebotRestartMessage: "",
    typebotSlug: "",
    typebotUnknownMessage: "",
    assistantId: "",
    flowId: "",
  };

  const [integration, setIntegration] = useState(initialState);
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [flows, setFlows] = useState([]); // Estado para armazenar os flows
  const [loadingFlows, setLoadingFlows] = useState(false); // Estado para carregar flows

  // Função para carregar os flows do FlowBuilder
  const loadFlows = async () => {
    try {
      setLoadingFlows(true);
      console.log("🔍 DEBUG loadFlows - Iniciando chamada para /flowbuilder");
      const response = await api.get("/flowbuilder");
      console.log("🔍 DEBUG loadFlows - Resposta recebida:", response);
      console.log("🔍 DEBUG loadFlows - response.data:", response.data);
      console.log("🔍 DEBUG loadFlows - Tipo de response.data:", typeof response.data);
      
      // Verificar se a resposta tem o formato esperado
      if (response.data && response.data.flows) {
        console.log("🔍 DEBUG loadFlows - Flows encontrados:", response.data.flows);
        console.log("🔍 DEBUG loadFlows - Quantidade de flows:", response.data.flows.length);
        setFlows(response.data.flows);
      } else {
        console.warn("⚠️ DEBUG loadFlows - Formato de resposta inesperado:", response.data);
        setFlows([]);
      }
    } catch (err) {
      console.error("❌ DEBUG loadFlows - Erro ao carregar flows:", err);
      console.error("❌ DEBUG loadFlows - Detalhes do erro:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      toastError(err);
      setFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  };

  const loadAssistants = async () => {
    try {
      setLoadingAssistants(true);
      console.log("🔍 DEBUG loadAssistants - Iniciando chamada para /assistants");
      
      // Verificar se há token de autenticação
      const token = localStorage.getItem("token");
      console.log("🔍 DEBUG loadAssistants - Token de autenticação:", {
        hasToken: !!token,
        tokenLength: token ? JSON.parse(token)?.length : 0
      });
      
      // Adicionar timeout e tratamento mais detalhado
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 segundos timeout
      
      // Remover o header x-debug-source que estava causando problemas de CORS
      const response = await api.get("/assistants", {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log("🔍 DEBUG loadAssistants - Resposta recebida:", {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });
      
      // Verificar se a resposta tem o formato esperado
      if (!response.data || typeof response.data !== 'object') {
        console.error("❌ DEBUG loadAssistants - Formato de resposta inválido:", typeof response.data);
        throw new Error("Formato de resposta inválido");
      }
      
      setAssistants(response.data.assistants || []);
    } catch (err) {
      console.error("❌ DEBUG loadAssistants - Erro ao carregar assistentes:", err);
      console.error("❌ DEBUG loadAssistants - Detalhes do erro:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers
      });
      
      // Tratamento específico para erro de CORS
      if (err.message && err.message.includes('CORS')) {
        toastUtils.error("Erro de permissão. Contate o administrador.", "Erro de Acesso");
      } else if (err.code === 'ERR_NETWORK') {
        toastUtils.error("Erro de conexão. Verifique sua internet.", "Erro de Conexão");
      } else if (err.code === 'ERR_BAD_RESPONSE') {
        toastUtils.error("Erro no servidor. Tente novamente.", "Erro no Servidor");
      } else {
        toastUtils.error("Erro ao carregar assistentes", "Erro nos Assistentes");
      }
      
      setAssistants([]);
    } finally {
      setLoadingAssistants(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!integrationId) return;
      try {
        const { data } = await api.get(`/queueIntegration/${integrationId}`);
        setIntegration((prevState) => {
          return { ...prevState, ...data };
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setIntegration(initialState);
    };
  }, [integrationId, open]);

  useEffect(() => {
    if (open) {
      console.log("🔍 DEBUG useEffect - Modal aberto, tipo:", integration.type);
      loadAssistants();
      // Carregar flows sempre que o modal abrir
      loadFlows();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setIntegration(initialState);
  };

  const handleTestSession = async (event, values) => {
    try {
      const { projectName, jsonContent, language } = values;

      await api.post(`/queueIntegration/testSession`, {
        projectName,
        jsonContent,
        language,
      });

      toastUtils.success(i18n.t("queueIntegrationModal.messages.testSuccess"), "Teste Realizado");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveDialogflow = async (values) => {
    try {
      // Para flowbuilder, usar o nome do flow selecionado
      if (values.type === "flowbuilder" && values.flowId) {
        const selectedFlow = flows.find(flow => flow.id === parseInt(values.flowId));
        if (selectedFlow) {
          values.name = selectedFlow.name;
          values.projectName = selectedFlow.name;
        } else {
          toastUtils.error("Flow selecionado não encontrado", "Erro");
          return;
        }
      } else if (
        values.type === "n8n" ||
        values.type === "webhook" ||
        values.type === "typebot" ||
        values.type === "openai" ||
        values.type === "assistant"
      ) {
        values.projectName = values.name;
      }
      
      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, values);
        toastUtils.success(i18n.t("queueIntegrationModal.messages.editSuccess"), "Integração Salva");
      } else {
        await api.post("/queueIntegration", values);
        toastUtils.success(i18n.t("queueIntegrationModal.messages.addSuccess"), "Integração Salva");
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          {integrationId
            ? `${i18n.t("queueIntegrationModal.title.edit")}`
            : `${i18n.t("queueIntegrationModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={DialogflowSchema}
          onSubmit={(values, actions, event) => {
            setTimeout(() => {
              handleSaveDialogflow(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <Paper square className={classes.mainPaper} elevation={1}>
                <DialogContent dividers>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        margin="dense"
                        fullWidth
                      >
                        <InputLabel id="type-selection-input-label">
                          {i18n.t("queueIntegrationModal.form.type")}
                        </InputLabel>

                        <Field
                          as={Select}
                          label={i18n.t("queueIntegrationModal.form.type")}
                          name="type"
                          labelId="profile-selection-label"
                          error={touched.type && Boolean(errors.type)}
                          helpertext={touched.type && errors.type}
                          id="type"
                          required
                        >
                          <MenuItem value="dialogflow">DialogFlow</MenuItem>
                          <MenuItem value="n8n">N8N</MenuItem>
                          <MenuItem value="webhook">WebHooks</MenuItem>
                          <MenuItem value="typebot">Typebot</MenuItem>
                          <MenuItem value="flowbuilder">Flowbuilder</MenuItem>
                          <MenuItem value="openai">OpenAI</MenuItem>
                          <MenuItem value="assistant">Assistente</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    {values.type === "dialogflow" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            fullWidth
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            margin="dense"
                            fullWidth
                          >
                            <InputLabel id="language-selection-input-label">
                              {i18n.t("queueIntegrationModal.form.language")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t(
                                "queueIntegrationModal.form.language"
                              )}
                              name="language"
                              labelId="profile-selection-label"
                              fullWidth
                              error={
                                touched.language && Boolean(errors.language)
                              }
                              helpertext={touched.language && errors.language}
                              id="language-selection"
                              required
                            >
                              <MenuItem value="pt-BR">Portugues</MenuItem>
                              <MenuItem value="en">Inglês</MenuItem>
                              <MenuItem value="es">Español</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.projectName"
                            )}
                            name="projectName"
                            error={
                              touched.projectName && Boolean(errors.projectName)
                            }
                            helpertext={
                              touched.projectName && errors.projectName
                            }
                            fullWidth
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.jsonContent"
                            )}
                            type="jsonContent"
                            multiline
                            //inputRef={greetingRef}
                            maxRows={5}
                            minRows={5}
                            fullWidth
                            name="jsonContent"
                            error={
                              touched.jsonContent && Boolean(errors.jsonContent)
                            }
                            helpertext={
                              touched.jsonContent && errors.jsonContent
                            }
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                      </>
                    )}

                    {(values.type === "n8n" || values.type === "webhook") && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            required
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helpertext={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                           
                          />
                        </Grid>
                      </>
                    )}

                    {values.type === "flowbuilder" && (
                      <Grid item xs={12} md={6} xl={6}>
                        <FormControl
                          variant="outlined"
                          className={classes.formControl}
                          margin="dense"
                          fullWidth
                        >
                          <InputLabel id="flow-selection-input-label">
                            {i18n.t("queueIntegrationModal.form.flowSelection")}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("queueIntegrationModal.form.flowSelection")}
                            name="flowId"
                            labelId="flow-selection-label"
                            error={touched.flowId && Boolean(errors.flowId)}
                            helpertext={touched.flowId && errors.flowId}
                            id="flowId"
                            required
                            disabled={loadingFlows}
                            onOpen={() => {
                              console.log("🔍 DEBUG Select onOpen - Flows disponíveis:", flows);
                              console.log("🔍 DEBUG Select onOpen - Quantidade:", flows.length);
                              console.log("🔍 DEBUG Select onOpen - Loading:", loadingFlows);
                            }}
                          >
                            <MenuItem value="">
                              <em>{i18n.t("queueIntegrationModal.form.selectFlow")}</em>
                            </MenuItem>
                            {flows && flows.length > 0 ? (
                              flows.map((flow) => (
                                <MenuItem key={flow.id} value={flow.id.toString()}>
                                  {flow.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>
                                <em>Nenhum flow disponível</em>
                              </MenuItem>
                            )}
                          </Field>
                          {loadingFlows && (
                            <CircularProgress size={20} style={{ marginTop: 8 }} />
                          )}
                          {!loadingFlows && flows.length === 0 && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                              Nenhum flow encontrado
                            </div>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {values.type === "typebot" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helpertext={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotSlug"
                            )}
                            name="typebotSlug"
                            error={
                              touched.typebotSlug && Boolean(errors.typebotSlug)
                            }
                            helpertext={
                              touched.typebotSlug && errors.typebotSlug
                            }
                            required
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotExpires"
                            )}
                            name="typebotExpires"
                            error={
                              touched.typebotExpires &&
                              Boolean(errors.typebotExpires)
                            }
                            helpertext={
                              touched.typebotExpires && errors.typebotExpires
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotDelayMessage"
                            )}
                            name="typebotDelayMessage"
                            error={
                              touched.typebotDelayMessage &&
                              Boolean(errors.typebotDelayMessage)
                            }
                            helpertext={
                              touched.typebotDelayMessage &&
                              errors.typebotDelayMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotKeywordFinish"
                            )}
                            name="typebotKeywordFinish"
                            error={
                              touched.typebotKeywordFinish &&
                              Boolean(errors.typebotKeywordFinish)
                            }
                            helpertext={
                              touched.typebotKeywordFinish &&
                              errors.typebotKeywordFinish
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotKeywordRestart"
                            )}
                            name="typebotKeywordRestart"
                            error={
                              touched.typebotKeywordRestart &&
                              Boolean(errors.typebotKeywordRestart)
                            }
                            helpertext={
                              touched.typebotKeywordRestart &&
                              errors.typebotKeywordRestart
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotUnknownMessage"
                            )}
                            name="typebotUnknownMessage"
                            error={
                              touched.typebotUnknownMessage &&
                              Boolean(errors.typebotUnknownMessage)
                            }
                            helpertext={
                              touched.typebotUnknownMessage &&
                              errors.typebotUnknownMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotRestartMessage"
                            )}
                            name="typebotRestartMessage"
                            error={
                              touched.typebotRestartMessage &&
                              Boolean(errors.typebotRestartMessage)
                            }
                            helpertext={
                              touched.typebotRestartMessage &&
                              errors.typebotRestartMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                           
                          />
                        </Grid>
                      </>
                    )}
                    {values.type === "openai" && (
                      <>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            fullWidth
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label="Configurações JSON (API Key, Model, etc)"
                            type="text"
                            multiline
                            maxRows={10}
                            minRows={5}
                            fullWidth
                            name="jsonContent"
                            error={
                              touched.jsonContent && Boolean(errors.jsonContent)
                            }
                            helpertext={
                              touched.jsonContent && errors.jsonContent
                            }
                            variant="outlined"
                            margin="dense"
                            placeholder='{"apiKey": "sk-...", "model": "gpt-4", "temperature": 0.7}'
                          />
                        </Grid>
                      </>
                    )}
                    {values.type === "assistant" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            fullWidth
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            margin="dense"
                            fullWidth
                          >
                            <InputLabel id="assistant-selection-input-label">
                              {i18n.t("queueIntegrationModal.form.assistantSelection")}
                            </InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("queueIntegrationModal.form.assistantSelection")}
                              name="assistantId"
                              labelId="assistant-selection-label"
                              error={touched.assistantId && Boolean(errors.assistantId)}
                              helpertext={touched.assistantId && errors.assistantId}
                              id="assistant-selection"
                              disabled={loadingAssistants}
                              required
                            >
                              <MenuItem value="">
                                <em>{i18n.t("queueIntegrationModal.form.noAssistantSelected")}</em>
                              </MenuItem>
                              {assistants.map((assistant) => (
                                <MenuItem key={assistant.id} value={assistant.id}>
                                  {assistant.name}
                                </MenuItem>
                              ))}
                            </Field>
                            {loadingAssistants && (
                              <CircularProgress size={20} style={{ marginTop: 8 }} />
                            )}
                          </FormControl>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </DialogContent>
              </Paper>

              <DialogActions>
                {values.type === "dialogflow" && (
                  <Button
                    //type="submit"
                    onClick={(e) => handleTestSession(e, values)}
                    color="inherit"
                    disabled={isSubmitting}
                    name="testSession"
                    variant="outlined"
                    className={classes.btnLeft}
                  >
                    {i18n.t("queueIntegrationModal.buttons.test")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("queueIntegrationModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {integrationId
                    ? `${i18n.t("queueIntegrationModal.buttons.okEdit")}`
                    : `${i18n.t("queueIntegrationModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QueueIntegrationModal;